"use client";

import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../@/components/ui/select";
import { toast } from "sonner";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import axios from "axios";
import { api_endpoint } from "../constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import { Form, FormField } from "@/components/ui/form";
import { Field, FieldContent, FieldError, FieldLabel } from "../../@/components/ui/field";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";

// Validation schema
const editSkillSchema = z.object({
    name: z.string().min(1, "This is required"),
    description: z.string().optional(),
    parentSkillId: z
        .union([z.literal(""), z.coerce.number().int().positive("Parent ID must be a number")])
        .optional(),
});

export default function EditSkillDialog({ 
    skill, 
    skills = [], 
    onUpdated, 
    trigger = null 
}) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { getToken } = useAuth();

    const form = useForm({
        resolver: zodResolver(editSkillSchema),
        defaultValues: {
            name: skill?.name || "",
            description: skill?.description || "",
            parentSkillId: skill?.parent_skill_id ? String(skill.parent_skill_id) : "",
        },
    });

    // Update form values if category prop changes
    useEffect(() => {
        form.reset({
            name: skill?.name || "",
            description: skill?.description || "",
            parentSkillId: skill?.parent_skill_id ? String(skill.parent_skill_id) : "",
        });
    }, [skill, form]);

    // Get parent categories for dropdown (exclude self and deeply nested ones)
    const getParentOptions = () => {
        if (!Array.isArray(skills)) return [];
        return skills.filter(cat => (cat?.level ?? 0) < 2 && cat.id !== skill?.id);
    };

    const getPrefix = (level) => {
        if (level === 0) return "";
        if (level === 1) return "— ";
        if (level === 2) return "— — ";
        return "— ".repeat(level);
    };

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            const token = await getToken();
            if (!token) return;

            const payload = {
                id: skill.id,
                name: data.name.trim(),
                description: data.description?.trim() || "",
                parent_skill_id:
                    data.parentSkillId && data.parentSkillId !== ""
                        ? Number(data.parentSkillId)
                        : null,
            };

            console.log("Payload for update:", payload);
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            };

            const response = await axios.put(api_endpoint + `/course/skill/update/${skill.id}`, payload, reqConfig);
            toast.success("Skill updated successfully");
            setOpen(false);
            if (onUpdated) onUpdated();
        } catch (error) {
            console.error("Error updating skill:", error);
            toast.error("Failed to update skill. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.reset();
        setOpen(false);
    };

    const parentSelectValue = form.watch("parentSkillId")
        ? String(form.watch("parentSkillId"))
        : "none";

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger ? trigger : (
                    <Button variant="outline" size="sm">
                        Edit
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="h-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-(--neutral-900)">
                        Edit Skill
                    </DialogTitle>
                    <DialogDescription className="font-body text-(--neutral-700)">
                        Edit the skill details below.
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field, fieldState }) => (
                                <Field data-disabled={loading}>
                                    <FieldLabel>Name *</FieldLabel>
                                    <FieldContent>
                                        <Input
                                            {...field}
                                            id="name"
                                            placeholder="Enter skill title"
                                            className={cn(
                                                "font-body",
                                                fieldState.error &&
                                                    "border-(--color-destructive) focus-visible:ring-(--color-destructive)"
                                            )}
                                            disabled={loading}
                                        />
                                        <FieldError
                                            errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                        />
                                    </FieldContent>
                                </Field>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field, fieldState }) => (
                                <Field data-disabled={loading}>
                                    <FieldLabel>
                                        Description
                                        <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                                            <Info className="w-3 h-3" />
                                            Optional
                                        </div>
                                    </FieldLabel>
                                    <FieldContent>
                                        <Textarea
                                            {...field}
                                            id="description"
                                            placeholder="Enter skill description"
                                            rows={4}
                                            className={cn(
                                                "font-body resize-none",
                                                fieldState.error &&
                                                    "border-(--color-destructive) focus-visible:ring-(--color-destructive)"
                                            )}
                                            disabled={loading}
                                        />
                                        <FieldError
                                            errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                        />
                                    </FieldContent>
                                </Field>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="parentSkillId"
                            render={({ field, fieldState }) => (
                                <Field data-disabled={loading}>
                                    <FieldLabel>
                                        Parent Skill
                                        <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                                            <Info className="w-3 h-3" />
                                            Optional
                                        </div>
                                    </FieldLabel>
                                    <FieldContent>
                                        <Select
                                            value={parentSelectValue}
                                            onValueChange={(value) => field.onChange(value === "none" ? "" : value)}
                                            disabled={loading}
                                        >
                                            <SelectTrigger
                                                className={cn(
                                                    "font-body w-full",
                                                    fieldState.error &&
                                                        "border-(--color-destructive) focus:ring-(--color-destructive)"
                                                )}
                                            >
                                                <SelectValue placeholder="Select parent skill" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">None (Root Skill)</SelectItem>
                                                {getParentOptions().map((skill) => (
                                                    <SelectItem key={skill.id} value={skill.id.toString()}>
                                                        {getPrefix(skill.level)}{skill.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FieldError
                                            errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                        />
                                    </FieldContent>
                                </Field>
                            )}
                        />

                        {/* Action Buttons */}
                        <DialogFooter className="">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleCancel}
                                disabled={loading}
                                className="font-body"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant=""
                                disabled={loading}
                                className="font-body"
                            >
                                {loading ? <ClipLoader size={20} color="#fff" /> : "Update Skill"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}